import React from "react";
import { Testimonial, TestimonialsComponent } from "../components/common/testimonial/TestimonialComponent";
import { JsonLd } from "../components/common/JsonLd";
import { RelatedPages } from "../components/common/RelatedPages";
import { SEOHelmet } from "../components/common/SEOHelmet";
import { PATH_PEDIATRICS } from "../utils/constants";
import pageData from "../data/pages/paediatrie.json";
import { mapPageSections } from "../utils/pageContent";
import { buildSeoPageGraph } from "../utils/structured-data";

type Section = {
  title: string;
  content: string[];
  img?: string;
  ratio?: string;
};

const data = {
  sections: mapPageSections(pageData.sections as Section[]),
  testimonials: pageData.testimonials as Testimonial[],
};

export default function PediatricsPage() {
  return (
    <>
      <SEOHelmet page="paediatrie" pathname={PATH_PEDIATRICS} />
      <JsonLd
        data={buildSeoPageGraph(PATH_PEDIATRICS, "paediatrie", {
          medicalSpecialty: "Pediatric",
        })}
      />

      <main className="space-y-14">
        {data.sections.map((section, index) => (
          <article key={index}>
            {index === 0 ? (
              <section className="grid lg:grid-cols-2 gap-12 items-start" aria-labelledby="main-heading">
                <div>
                  <h1
                    id="main-heading"
                    className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900 dark:text-white mb-6"
                  >
                    Pädiatrische Ergotherapie
                  </h1>
                  <h2 className="sr-only">Behandlungsschwerpunkte</h2>
                  <ul
                    className="list-disc marker:text-ergo-400 dark:marker:text-ergo-500 list-inside space-y-3 mb-8 text-base text-gray-500 dark:text-gray-400"
                    aria-label="Behandlungsschwerpunkte in der Pädiatrie"
                  >
                    {section.content.map((item, itemIndex) => (
                      <li key={itemIndex} className="text-gray-500 dark:text-gray-400 leading-relaxed">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                {section.img && (
                  <aside aria-label="Bildliche Darstellung der pädiatrischen Ergotherapie">
                    <img
                      src={section.img}
                      alt="Pädiatrische Ergotherapie Rottweil - Kindgerechte Behandlung bei Entwicklungsstörungen, ADHS und Verhaltensschwächen"
                      className="w-64 md:w-96 lg:w-124 rounded-2xl object-cover shadow-sm max-h-[600px] aspect-[3/4]"
                    />
                  </aside>
                )}
              </section>
            ) : (
              <section aria-labelledby={`section-${index}`}>
                <h2
                  id={`section-${index}`}
                  className="text-xl sm:text-2xl font-semibold tracking-tight text-ergo-600 dark:text-ergo-400 mb-4"
                >
                  {section.title}
                </h2>
                <ul
                  className="list-disc marker:text-ergo-400 dark:marker:text-ergo-500 list-inside space-y-3 mb-8 text-base text-gray-500 dark:text-gray-400"
                  aria-label="Behandlungsinhalte und Therapiemöglichkeiten"
                >
                  {section.content.map((item, itemIndex) => (
                    <li key={itemIndex} className="text-gray-500 dark:text-gray-400 leading-relaxed">
                      {item}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </article>
        ))}

        <section aria-label="Erfahrungsberichte">
          <h2 className="sr-only">Erfahrungsberichte unserer Klienten</h2>
          <TestimonialsComponent testimonials={data.testimonials} />
        </section>
        <RelatedPages pathname={PATH_PEDIATRICS} />
      </main>
    </>
  );
}
